import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, api, fetchAll } from "@/lib/apiClient";
import { DateRange, rangeLabel, useDateRange, withinRange } from "@/components/DateRange";
import { Download, CheckCircle2, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import { AdminTable, CustomerCell, CopyableId, DateTimeCell, PinButton, usePinToggle } from "./lib";

// Cart Orders was a separate page over these same purchases; it was merged in here, so a row now
// answers both "was I paid?" and "what for?". Items and Bill are what it brought with it.
const GRID = "grid-cols-[125px_100px_105px_minmax(140px,1fr)_90px_55px_90px_90px_55px_105px]";
const COLUMNS = [
  // One reference, not two.
  //
  // Receipt ID (the payment row's key) and Invoice No (what is printed on the customer's PDF) were
  // separate columns, which for a successful purchase is the same purchase twice — and only one of
  // the two has ever been seen by a customer. So this shows the invoice number when there is one.
  //
  // The fallback is not cosmetic: a **failed** payment never created an order and therefore has no
  // invoice number at all, and the receipt id is the only handle those rows have. Both remain
  // searchable, so pasting either one still finds the row.
  { key: "reference", label: "Reference" },
  { key: "txn", label: "Txn ID" },
  { key: "date", label: "Date" },
  { key: "customer", label: "Customer" },
  { key: "amount", label: "Amount" },
  { key: "items", label: "Items" },
  { key: "method", label: "Payment Type" },
  { key: "status", label: "Status" },
  { key: "bill", label: "Bill" },
  // Reconciliation: did the money actually reach the bank? A gateway "success" and a bank credit
  // are separate events, usually a day or two apart and net of fees.
  { key: "confirmed", label: "In bank" },
];

/**
 * Reconciling a batch of payments from a PayU settlement export.
 *
 * The gateway settles on T+1/T+2 in batches, net of its fee, so no bank line ever equals an invoice
 * total and matching them by eye does not survive contact with volume. Admin exports the settlement
 * from the PayU dashboard and drops the file here.
 *
 * **Preview, then apply.** The file is always uploaded with `dry_run` first and nothing is written
 * until the counts below have been looked at — this posts to the money ledger in bulk from a file
 * nobody has read line by line, so a silent one-click import is the wrong shape for it. The second
 * upload (the real one) sends the same bytes, which is why the File object is held rather than the
 * preview response.
 */
function SettlementImport({ onDone }) {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const picker = useRef(null);

  const send = async (f, dry) => {
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post(`/admin/payments/settlement-import?dry_run=${dry}`, fd,
                                    { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };
  const choose = async (e) => {
    const f = e.target.files[0]; e.target.value = ""; if (!f) return;
    setBusy(true);
    try { setFile(f); setPreview(await send(f, true)); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not read that file"); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await send(file, false);
      toast.success(`${r.applied} payment${r.applied === 1 ? "" : "s"} reconciled`);
      setPreview(null); setFile(null); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <input ref={picker} type="file" accept=".csv,text/csv" className="hidden" onChange={choose}
             data-testid="settlement-file"/>
      <Button variant="outline" size="sm" disabled={busy} data-testid="settlement-import"
              onClick={() => picker.current?.click()}>
        <Upload className="w-3.5 h-3.5 mr-1.5"/>{busy ? "Reading…" : "Import settlement"}
      </Button>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Settlement preview</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-4 text-sm" data-testid="settlement-preview">
              <div className="text-[#A0AABF]">
                <b className="text-white">{preview.rows}</b> rows read ·{" "}
                <b className="text-emerald-400">{preview.matched.length}</b> will reconcile ·{" "}
                <b className={preview.exceptions.length ? "text-red-400" : "text-white"}>
                  {preview.exceptions.length}</b> need attention
              </div>

              {/* Surfaced rather than ignored: a report whose amount column we failed to recognise
                  would otherwise import a page of rows with no money on them, which looks like it
                  worked. */}
              {preview.unknown_headers?.length > 0 && (
                <div className="text-xs text-amber-400 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-px"/>
                  <span>Columns not recognised and ignored: {preview.unknown_headers.join(", ")}.
                        Check nothing important is in them.</span>
                </div>
              )}

              {preview.exceptions.length > 0 && (
                <div className="max-h-48 overflow-auto rounded border border-red-500/20 bg-red-500/5 p-2 space-y-1">
                  {preview.exceptions.map((x, i) => (
                    <div key={i} className="text-xs text-[#E5E7EB]">
                      <span className="font-mono">{x.ref || "—"}</span>
                      <span className="text-red-400"> — {x.reason}</span>
                      {x.unexplained != null && <span className="text-[#6B7280]">
                        {" "}(invoiced ₹{x.invoiced}, received ₹{x.received}, ₹{x.unexplained} unexplained)</span>}
                    </div>
                  ))}
                </div>
              )}

              {preview.matched.length > 0 && (
                <div className="max-h-48 overflow-auto rounded border border-white/10 p-2 space-y-1">
                  {preview.matched.map((m, i) => (
                    <div key={i} className="text-xs text-[#A0AABF] flex justify-between gap-3">
                      <span className="font-mono truncate">{m.ref}</span>
                      <span className="shrink-0">
                        ₹{m.invoiced} → <b className="text-white">₹{m.received}</b>
                        {m.utr && <span className="text-[#6B7280]"> · {m.utr}</span>}
                        {m.was_reconciled && <span className="text-amber-400"> · re-confirms</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPreview(null)}>Cancel</Button>
                <Button disabled={busy || !preview.matched.length} onClick={apply}
                        data-testid="settlement-apply">
                  Reconcile {preview.matched.length} payment{preview.matched.length === 1 ? "" : "s"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AdminPayments() {
  const [rows, setRows] = useState([]);
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  // Same window as the dashboard and the queues, defaulted from Admin → Settings. This page had no
  // date filter at all, so "Collected" was a lifetime figure while the dashboard card that links
  // here reported a month — two numbers for the same word, on the money page.
  const { from, to, ready, setRange } = useDateRange(params);
  useEffect(() => {
    if (!ready) return;
    if (params.get("from") === from && params.get("to") === to) return;
    const next = new URLSearchParams(params);
    next.set("from", from); next.set("to", to);
    setParams(next, { replace: true });
  }, [ready, from, to, params, setParams]);
  const tok = localStorage.getItem("dt_token");
  const [reconcileFor, setReconcileFor] = useState(null);
  const [rec, setRec] = useState({ bank_ref: "", received_amount: "", note: "" });
  const load = () => fetchAll("/admin/payments").then(setRows).catch(() => {});
  const togglePin = usePinToggle("payment", setRows);
  useEffect(() => { load(); }, []);

  // Opening the dialog pre-fills the amount with what was invoiced — the common case is that the
  // full amount arrived, and the admin only edits it when fees or a partial settlement changed it.
  const openReconcile = (p) => {
    setRec({ bank_ref: p.bank_ref || "",
             received_amount: p.received_amount ?? p.amount ?? "",
             note: p.reconcile_note || "" });
    setReconcileFor(p);
  };
  const saveReconcile = async (confirmed) => {
    try {
      await api.put(`/admin/payments/${reconcileFor.id}/reconcile`,
                    confirmed ? { reconciled: true, ...rec } : { reconciled: false });
      toast.success(confirmed ? "Payment confirmed in bank" : "Confirmation removed");
      setReconcileFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  // `ts` is when the payment was taken — the date the row is sorted and reported by, and the one
  // an admin means by "collected in June". Not `bank_ref`'s settlement date, which lands a day or
  // two later and would put a payment in the wrong month whenever the two straddle a boundary.
  const windowed = useMemo(
    () => (ready ? rows.filter(p => withinRange(p.ts, from, to)) : rows), [rows, ready, from, to]);
  // What the table shows: the window plus anything pinned, which stays in view whatever the range
  // (see lib.jsx "Pins"). The totals below still read `windowed` — a pinned payment from March
  // must not add itself to this month's "Collected".
  const shown = useMemo(() => {
    const ids = new Set(windowed.map(p => p.id));
    return rows.filter(p => ids.has(p.id) || p.pinned);
  }, [rows, windowed]);
  const hidden = rows.length - shown.length;

  // Only money actually collected — a failed attempt is a row, not revenue.
  const collected = (list) => list.filter(r => r.status === "completed")
                                  .reduce((s, r) => s + (r.amount || 0), 0);
  // Both figures, always. A single "Collected" that silently means "in the selected range" is the
  // one number on this page nobody should have to check the date picker to interpret — and the
  // lifetime total is what reconciles against the books.
  const total = collected(windowed);
  const lifetime = collected(rows);

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="font-serif-display text-3xl md:text-4xl text-white">Payments &amp; Orders</h1>
        <div className="flex items-center gap-4">
          <SettlementImport onDone={load}/>
          <div className="text-sm text-[#6B7280]" data-testid="admin-payments-collected">
            Collected ({rangeLabel(from, to)}):{" "}
            <span className="font-serif-display text-xl text-[#CBA96C]">₹{total.toLocaleString()}</span>
            {lifetime !== total && (
              <span className="text-xs text-[#6B7280]"> · ₹{lifetime.toLocaleString()} all time</span>
            )}
          </div>
        </div>
      </div>
      <AdminTable
        testid="admin-payments" columns={COLUMNS} gridCols={GRID} rows={shown}
        defaultSort="date" pinnable
        toolbar={<DateRange from={from} to={to} onChange={setRange} testid="admin-payments-range" />}
        empty={hidden > 0
          ? `No payments in the ${rangeLabel(from, to)} — ${hidden} outside it.`
          : "No payments yet."}
        searchPlaceholder="Search invoice no, txn, receipt, customer, amount…"
        // `transaction_ids` as well as `transaction_id`: a cart checkout pays for several
        // documents in one payment, so it has no single txn id — searching for any one of them
        // still has to find the bill.
        matches={(p, q) => [
          // `payment_ref` is the gateway's own id — the number on the customer's invoice and on
          // their card statement, and therefore the one they quote and an auditor traces. It is
          // only the same as `p.id` while payments are mocked.
          p.id, p.payment_ref, p.bank_ref, p.invoice_no, p.order_id,
          p.transaction_id, ...(p.transaction_ids || []),
          p.customer_name, p.customer_email, p.customer_mobile,
          p.method, p.status, p.service_type, p.subtype, String(p.amount ?? ""),
        ].filter(Boolean).join(" ").toLowerCase().includes(q)}
        accessors={{
          reference: (p) => p.invoice_no || p.id,
          txn: (p) => p.transaction_id || "",
          date: (p) => new Date(p.ts).getTime(),
          customer: (p) => (p.customer_name || "").toLowerCase(),
          amount: (p) => p.amount ?? 0,
          method: (p) => p.method || "",
          status: (p) => p.status || "",
          items: (p) => p.items ?? 0,
          bill: (p) => (p.has_bill ? 1 : 0),
          confirmed: (p) => (p.reconciled ? 1 : 0),
        }}
        renderRow={(p) => (
          <>
            {/* A direct payment has exactly one transaction to jump to; a cart checkout's invoice
                covers every document bought in it (backend/CLAUDE.md "Every payment writes a
                payments row") and has none — `transaction_id` is null precisely in that case, so
                falling back to the Transactions queue searched to this invoice number (its search
                already matches on invoice/order id, see adminTxnMatchesSearch) surfaces all of
                them at once instead of picking one arbitrarily. */}
            <div className="flex items-start gap-1.5 min-w-0">
            <span className="pt-2"><PinButton pinned={p.pinned} onToggle={() => togglePin(p)} testid={`pin-payment-${p.id}`}/></span>
            <button
              type="button"
              onClick={() => p.transaction_id
                ? nav(`/admin/txn/${p.transaction_id}`)
                : nav(`/admin/transactions?q=${encodeURIComponent(p.invoice_no || p.id)}`)}
              title={p.transaction_id ? "Open this transaction" : "Find the transactions on this invoice"}
              data-testid={`reference-${p.id}`}
              className="pt-1.5 min-w-0 text-left hover:opacity-80 transition-opacity"
            >
              {p.invoice_no ? (
                <>
                  <div className="font-mono text-xs text-white truncate">{p.invoice_no}</div>
                  <div className="text-[10px] text-[#6B7280]">Invoice No</div>
                </>
              ) : (
                <>
                  <div className="font-mono text-xs text-[#A0AABF] truncate">{String(p.id).slice(0, 8)}</div>
                  <div className="text-[10px] text-[#6B7280]">Receipt — no invoice</div>
                </>
              )}
            </button>
            </div>
            <CopyableId value={p.transaction_id} label="Txn ID" testid={`copy-txn-${p.id}`}
                        onOpen={() => p.transaction_id && nav(`/admin/txn/${p.transaction_id}`)}/>
            <DateTimeCell value={p.ts}/>
            <CustomerCell t={p} onOpen={() => p.transaction_id && nav(`/admin/txn/${p.transaction_id}`)}/>
            <div className="text-sm text-white pt-1.5">₹{Number(p.amount ?? 0).toLocaleString()}</div>
            {/* A failed payment bought nothing — no order, no items, no bill. The dash is the
                honest cell rather than a zero, and those rows are exactly why this ledger is not
                just a list of completed orders. */}
            <div className="text-sm text-white pt-1.5">{p.items ?? "—"}</div>
            <div className="text-sm text-[#A0AABF] pt-1.5 capitalize">{p.method || "—"}</div>
            <div className="pt-1.5"><StatusPill status={p.status}/></div>
            <div className="pt-1.5">
              {p.has_bill ? (
                <a href={`${API}/orders/${p.order_id}/bill?_t=${tok}`} target="_blank" rel="noopener noreferrer"
                   data-testid={`bill-${p.id}`} title="Download bill PDF"
                   className="text-[#CBA96C] hover:opacity-80 inline-flex">
                  <Download className="w-4 h-4"/>
                </a>
              ) : <span className="text-xs text-[#4B5563]">—</span>}
            </div>
            <div className="pt-1.5">
              {p.status !== "completed" ? (
                <span className="text-xs text-[#4B5563]">—</span>
              ) : p.reconciled ? (
                <button onClick={() => openReconcile(p)} data-testid={`reconciled-${p.id}`}
                        title={`Confirmed by ${p.reconciled_by_name || "admin"}`}
                        className="inline-flex items-center gap-1 text-xs text-[#10B981] hover:underline">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {p.received_amount != null ? `₹${Number(p.received_amount).toLocaleString()}` : "Yes"}
                </button>
              ) : (
                <button onClick={() => openReconcile(p)} data-testid={`reconcile-${p.id}`}
                        className="text-xs text-[#A0AABF] border border-white/15 rounded-full px-2.5 py-1
                                   hover:text-white hover:border-[#CBA96C]/50 transition-colors">
                  Confirm
                </button>
              )}
            </div>
          </>
        )}
      />

      <Dialog open={!!reconcileFor} onOpenChange={(o) => !o && setReconcileFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm payment reached the bank</DialogTitle></DialogHeader>
          {reconcileFor && (
            <div className="space-y-4">
              <div className="text-xs text-[#6B7280] space-y-1">
                <div>Invoice <b className="text-white">{reconcileFor.invoice_no || "—"}</b>
                     {" · "}invoiced <b className="text-white">₹{Number(reconcileFor.amount ?? 0).toLocaleString()}</b></div>
                <div className="font-mono break-all">Payment Txn No: {reconcileFor.payment_ref || reconcileFor.id}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Bank reference / UTR</Label>
                <Input value={rec.bank_ref} data-testid="reconcile-bank-ref"
                       onChange={e => setRec(r => ({ ...r, bank_ref: e.target.value }))}
                       placeholder="From the bank statement"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Amount received</Label>
                <Input type="number" step="0.01" value={rec.received_amount} data-testid="reconcile-amount"
                       onChange={e => setRec(r => ({ ...r, received_amount: e.target.value }))}/>
                {/* Said here rather than discovered at year end: gateways settle net of fees, so
                    this is normally a little less than the invoice. */}
                <div className="text-[11px] text-[#6B7280] mt-1">
                  Usually less than the invoice — gateways settle net of their fee.
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Note (optional)</Label>
                <Input value={rec.note} data-testid="reconcile-note"
                       onChange={e => setRec(r => ({ ...r, note: e.target.value }))}/>
              </div>
              <div className="flex justify-between gap-2 pt-2">
                {reconcileFor.reconciled ? (
                  <Button variant="outline" data-testid="reconcile-undo"
                          onClick={() => saveReconcile(false)}>Remove confirmation</Button>
                ) : <span />}
                <Button data-testid="reconcile-save" onClick={() => saveReconcile(true)}
                        className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
                  Confirm received
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
